"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

const STATUS_ENDPOINT = "https://dash0status.com/api/v2/status.json";
const CACHE_KEY = "dash0-system-status";
const CACHE_TTL_MS = 60_000; // Re-check availability at most once per minute per visitor.

const LOADING_STATUS = "Fetching status...";

type CachedStatus = { value: string; expiresAt: number };

// Module-scoped so all mounts in the same tab share one request. Client-side
// navigation between docs pages reuses the in-flight promise instead of
// starting a new availability check.
let inflight: Promise<string> | null = null;

function readCache(): string | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedStatus;
    if (cached.expiresAt > Date.now()) return cached.value;
  } catch {
    // Ignore unavailable or malformed storage (private mode, quota, bad JSON).
  }
  return null;
}

function writeCache(value: string) {
  try {
    const entry: CachedStatus = { value, expiresAt: Date.now() + CACHE_TTL_MS };
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Ignore storage write failures (private mode, quota).
  }
}

async function fetchStatus(): Promise<string> {
  let value: string;
  try {
    const response = await fetch(STATUS_ENDPOINT, { redirect: "follow" });
    const data = await response.json();
    value = data.status.description || "Status unavailable";
  } catch (error) {
    console.error(error);
    value = "Status unavailable";
  }
  // Cache both healthy and failed results so an outage does not make every
  // subsequent navigation re-hit the status endpoint.
  writeCache(value);
  return value;
}

function getStatus(): Promise<string> {
  const cached = readCache();
  if (cached) return Promise.resolve(cached);
  if (!inflight) {
    inflight = fetchStatus().finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

const statusColors = {
  online: {
    dot: "#75C88B",
    pulse: "#3A6446",
  },
  loading: {
    dot: "#2C2C2C",
    pulse: "#424242",
  },
  warning: {
    dot: "#facc15",
    pulse: "#a16207",
  },
};

const getStatusType = (status: string): 'online' | 'loading' | 'warning' => {
  switch (status) {
    case "All systems online":
    case "All Systems Operational":
      return "online";
    case "Fetching status...":
      return "loading";
    default:
      return "warning";
  }
};


export function SystemStatusIndicator() {
  const [status, setStatus] = useState<string>(LOADING_STATUS);

  useEffect(() => {
    let active = true;
    getStatus().then((value) => {
      if (active) setStatus(value);
    });
    return () => {
      active = false;
    };
  }, []);

  const statusType = getStatusType(status);

  return (
    <Link
      href="https://dash0status.com/"
      className="text-sm font-medium flex gap-2 items-center self-start"
      target="_blank"
    >
      <div className="w-3 rounded-full flex items-center justify-center relative">
        <div
          style={{
            backgroundColor: statusColors[statusType].dot,
          }}
          className="w-1.5 animate-none aspect-square rounded-full absolute z-10"
        />
        <div
          style={{
            backgroundColor: statusColors[statusType].pulse,
          }}
          className="w-3 animate-ping motion-reduce:animate-none absolute aspect-square rounded-full bg-teal-800"
        />
      </div>
      {status}
    </Link>
  );
}

export function SystemStatus() {
  return (
    <div className="sticky bottom-0 from-transparent z-10 pt-4 pb-4 to-grey-950 bg-linear-to-b bg-grey-950 hidden lg:flex">
      <SystemStatusIndicator />
    </div>
  );
}
